/**
 * Invitation Service — API layer for the invitation system.
 *
 * All functions return result objects { success, ...data } (no throwing).
 * Follows the same pattern as processingService.js.
 */
import { getAuthHeaders } from "./apiUtils";
import { ServerURI } from "../globals";

/**
 * Create a new invitation.
 * @param {{ email: string, role: string, message?: string }} params
 * @returns {Promise<{ success: boolean, data?: Object, error?: string }>}
 */
export async function createInvitation({ email, role, message }) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ServerURI}/api/invitations`, {
      method: "POST",
      headers,
      body: JSON.stringify({ email, role, message: message || undefined }),
    });

    const body = await response.json();

    if (response.ok) {
      return { success: true, data: body };
    }

    return { success: false, error: body.error || `Error ${response.status}` };
  } catch (error) {
    console.error("[invitationService] createInvitation error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * List invitations created by the current user.
 * @returns {Promise<{ success: boolean, invitations?: Array, error?: string }>}
 */
export async function listInvitations() {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ServerURI}/api/invitations`, {
      method: "GET",
      headers,
    });

    const body = await response.json();

    if (response.ok) {
      return { success: true, invitations: body.invitations || [] };
    }

    return { success: false, error: body.error || `Error ${response.status}` };
  } catch (error) {
    console.error("[invitationService] listInvitations error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Resend an invitation (generates a new token + expiry).
 * @param {string} id - Invitation document ID
 * @returns {Promise<{ success: boolean, inviteLink?: string, emailSent?: boolean, emailDeliveryStatus?: string, error?: string }>}
 */
export async function resendInvitation(id) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ServerURI}/api/invitations/${id}/resend`, {
      method: "POST",
      headers,
    });

    const body = await response.json();

    if (response.ok) {
      return {
        success: true,
        inviteLink: body.inviteLink,
        emailSent: body.emailSent,
        emailDeliveryStatus: body.emailDeliveryStatus,
      };
    }

    return { success: false, error: body.error || `Error ${response.status}` };
  } catch (error) {
    console.error("[invitationService] resendInvitation error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Revoke a pending invitation.
 * @param {string} id - Invitation document ID
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function revokeInvitation(id) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ServerURI}/api/invitations/${id}/revoke`, {
      method: "POST",
      headers,
    });

    const body = await response.json();

    if (response.ok) {
      return { success: true };
    }

    return { success: false, error: body.error || `Error ${response.status}` };
  } catch (error) {
    console.error("[invitationService] revokeInvitation error:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Finalize an invitation after Firebase Auth and the user profile exist.
 * The backend derives the accepted UID and authoritative invite fields from
 * the caller's Firebase token.
 */
export async function acceptInvitation(token) {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${ServerURI}/api/invitations/accept`, {
      method: "POST",
      headers,
      body: JSON.stringify({ token }),
    });
    const body = await response.json();

    if (response.ok) return { success: true };
    return { success: false, error: body.error || `Error ${response.status}` };
  } catch (error) {
    console.error("[invitationService] acceptInvitation error:", error);
    return { success: false, error: error.message };
  }
}
